import axios, { type AxiosInstance } from 'axios';
import { detectCardBrand, getLastFourDigits, type CardBrand } from '~/utils/creditCard';

export type CardInput = {
  number: string;
  holderName: string;
  /** `MM/AA` */
  expiry: string;
  cvv: string;
};

export type CardToken = {
  /** Token do Pagar.me: é o único dado do cartão que segue para a API Aride (`hash_card`). */
  token: string;
  /** Validade do token; depois dela é preciso tokenizar de novo. */
  expiresAt: string | null;
  brand: CardBrand;
  lastFour: string;
};

export type CardField = 'number' | 'expiry' | 'cvv' | 'name';

/** Erro de tokenização já com a mensagem e o campo do formulário onde ele deve aparecer. */
export class CardTokenizationError extends Error {
  readonly kind: 'missing-public-key' | 'rejected' | 'network';
  readonly field: CardField | null;

  constructor(kind: CardTokenizationError['kind'], message: string, field: CardField | null = null) {
    super(message);
    this.name = 'CardTokenizationError';
    this.kind = kind;
    this.field = field;
  }
}

const FIELD_MESSAGES: Record<CardField, string> = {
  number: 'Confira o número do cartão.',
  expiry: 'Confira a validade do cartão.',
  cvv: 'Confira o código de segurança.',
  name: 'Confira o nome impresso no cartão.'
};

function fieldFromErrorKey(key: string): CardField | null {
  if (/number/i.test(key)) return 'number';
  if (/exp_(month|year)/i.test(key)) return 'expiry';
  if (/cvv/i.test(key)) return 'cvv';
  if (/holder/i.test(key)) return 'name';
  return null;
}

type TokenResponse = {
  id: string;
  expires_at?: string | null;
  card?: { last_four_digits?: string; brand?: string };
};

/**
 * Tokeniza o cartão no Pagar.me direto do navegador (`POST /tokens?appId={publicKey}`, `type: card`).
 * Os dados do cartão não passam pela API Aride: só o token volta para o fluxo.
 */
export async function tokenizeCard({
  apiUrl,
  publicKey,
  card,
  http = axios
}: {
  apiUrl: string;
  publicKey: string | null | undefined;
  card: CardInput;
  http?: AxiosInstance;
}): Promise<CardToken> {
  if (!publicKey) {
    throw new CardTokenizationError(
      'missing-public-key',
      'Pagamento indisponível: a chave do meio de pagamento desta organização não foi encontrada. Fale com o atendimento.'
    );
  }
  const [month, year] = card.expiry.split('/');
  try {
    const { data } = await http.post<TokenResponse>(`${apiUrl}/tokens?appId=${encodeURIComponent(publicKey)}`, {
      type: 'card',
      card: {
        number: card.number.replace(/\D/g, ''),
        holder_name: card.holderName.trim(),
        exp_month: Number(month),
        exp_year: Number(year),
        cvv: card.cvv.trim()
      }
    });
    const brand = detectCardBrand(card.number);
    const apiBrand = String(data.card?.brand ?? '').toLowerCase() as CardBrand;
    return {
      token: data.id,
      expiresAt: data.expires_at ?? null,
      brand: brand === 'unknown' && apiBrand ? apiBrand : brand,
      lastFour: data.card?.last_four_digits || getLastFourDigits(card.number)
    };
  } catch (error: unknown) {
    const response = (error as { response?: { status?: number; data?: { errors?: Record<string, unknown> } } })
      .response;
    if (!response) {
      throw new CardTokenizationError(
        'network',
        'Não foi possível validar o cartão agora. Verifique sua conexão e tente novamente.'
      );
    }
    const field = Object.keys(response.data?.errors ?? {})
      .map(fieldFromErrorKey)
      .find((found): found is CardField => found !== null);
    throw new CardTokenizationError(
      'rejected',
      field ? FIELD_MESSAGES[field] : 'Não foi possível validar este cartão. Confira os dados ou use outro cartão.',
      field ?? null
    );
  }
}
